import { useState } from "react";
import { Link } from "react-router-dom";
import { useDecaReport, useExportReport, useTenants, useUpdateIncident, useUsers, type ReportRow } from "../api/hooks";
import { DataTable, createColumnHelper } from "../components/DataTable";
import { Button, Chip, ErrorNotice, Field, Input, Notice, PageHeader, Select, StatCard } from "../components/ui";
import { IconChecklist, IconDocument, IconWarning } from "../components/icons";
import { decaStatusLabel, decaStatusTone, formatDate } from "../lib/format";

/**
 * Reportes (CLAUDE.md Fase 4, SKILL.md sección 10).
 *
 * Punto que la norma insiste en no mezclar: "con correcciones" y "con
 * incidencia" son **dos preguntas distintas** y se muestran como columnas
 * separadas, nunca fusionadas en un semáforo único.
 *  - Correcciones: automático (más de una versión, o documento sustituido).
 *  - Incidencia: manual, la marca un gestor con sus notas.
 *
 * El super-admin ve todos los tenants y puede filtrar por uno; un usuario
 * normal queda fijado al suyo (la API lo fuerza, no la interfaz).
 */

const column = createColumnHelper<ReportRow>();

export function ReportsPage() {
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [status, setStatus] = useState("");
  const [corrections, setCorrections] = useState("");
  const [incidents, setIncidents] = useState("");
  const [tenantId, setTenantId] = useState("");
  const [authorUserId, setAuthorUserId] = useState("");

  const tenants = useTenants();
  const isSuperAdmin = tenants.data?.canCrossTenants === true;
  const users = useUsers(tenantId || undefined);

  const filters = {
    ...(dateFrom ? { dateFrom: new Date(`${dateFrom}T00:00:00`).toISOString() } : {}),
    ...(dateTo ? { dateTo: new Date(`${dateTo}T23:59:59`).toISOString() } : {}),
    ...(status ? { status } : {}),
    ...(corrections ? { hasCorrections: corrections === "true" } : {}),
    ...(incidents ? { hasIncidents: incidents === "true" } : {}),
    ...(tenantId ? { tenantId } : {}),
    ...(authorUserId ? { authorUserId } : {}),
  };

  const report = useDecaReport(filters);
  const exportReport = useExportReport();
  const updateIncident = useUpdateIncident();

  const columns = [
    column.accessor("urlToken", {
      header: "Referencia",
      cell: (info) => (
        <Link to={`/deca/${info.row.original.id}`} className="ident text-brand-700 hover:underline dark:text-brand-300">
          {info.getValue()}
        </Link>
      ),
    }),
    column.accessor("status", {
      header: "Estado",
      cell: (info) => <Chip tone={decaStatusTone(info.getValue())}>{decaStatusLabel(info.getValue())}</Chip>,
    }),
    column.accessor("createdAt", {
      header: "Emitido",
      cell: (info) => <span className="num">{formatDate(info.getValue())}</span>,
    }),
    column.accessor("cargadorContractual", {
      header: "Cargador",
      cell: (info) => info.getValue() ?? "—",
    }),
    column.accessor("shipmentCount", {
      header: "Envíos",
      cell: (info) => <span className="num">{info.getValue()}</span>,
    }),
    column.accessor("versionsCount", {
      header: "Versiones",
      cell: (info) => <span className="num">{info.getValue()}</span>,
    }),
    column.accessor("hasCorrections", {
      header: "Correcciones",
      cell: (info) => (info.getValue() ? <Chip tone="warn">Con correcciones</Chip> : <span>—</span>),
    }),
    column.display({
      id: "incidents",
      header: "Incidencia",
      cell: (info) => {
        const row = info.row.original;
        return (
          <div className="space-y-1">
            <Button
              disabled={updateIncident.isPending}
              onClick={() =>
                updateIncident.mutate({
                  id: row.id,
                  hasIncidents: !row.hasIncidents,
                  incidentNotes: row.hasIncidents ? null : "Marcada desde el reporte",
                })
              }
            >
              {row.hasIncidents ? "Quitar marca" : "Marcar"}
            </Button>
            {row.hasIncidents ? <Chip tone="danger">Con incidencia</Chip> : null}
            {row.incidentNotes ? (
              <p className="max-w-[18rem] text-xs text-ink-500 dark:text-ink-400">{row.incidentNotes}</p>
            ) : null}
          </div>
        );
      },
    }),
  ];

  return (
    <>
      <PageHeader
        eyebrow="Control"
        title="Reporte de documentos"
        description="Cruce de documentos, versiones y auditoría. Las correcciones se detectan solas (más de una versión o documento sustituido); la incidencia la marca una persona."
        actions={
          <>
            <Button
              disabled={exportReport.isPending}
              onClick={() => exportReport.mutate({ format: "csv", filters })}
            >
              Exportar CSV
            </Button>
            <Button
              disabled={exportReport.isPending}
              onClick={() => exportReport.mutate({ format: "xlsx", filters })}
            >
              Exportar Excel
            </Button>
          </>
        }
      />

      <div className="surface mb-4 grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-3">
        {isSuperAdmin ? (
          <Field label="Empresa" htmlFor="rp-tenant" hint="Como super-admin puedes verlas todas o una sola.">
            <Select
              id="rp-tenant"
              value={tenantId}
              onChange={(event) => {
                setTenantId(event.target.value);
                setAuthorUserId("");
              }}
            >
              <option value="">Todas las empresas</option>
              {(tenants.data?.items ?? []).map((tenant) => (
                <option key={tenant.id} value={tenant.id}>
                  {tenant.name}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}

        <Field label="Autor" htmlFor="rp-author">
          <Select id="rp-author" value={authorUserId} onChange={(event) => setAuthorUserId(event.target.value)}>
            <option value="">Cualquiera</option>
            {(users.data?.items ?? []).map((user) => (
              <option key={user.id} value={user.id}>
                {user.fullName}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Estado" htmlFor="rp-status">
          <Select id="rp-status" value={status} onChange={(event) => setStatus(event.target.value)}>
            <option value="">Todos</option>
            <option value="ACTIVO">Activo</option>
            <option value="CERRADO">Cerrado</option>
            <option value="DESCARGA_DESACTIVADA">Descarga desactivada</option>
            <option value="SUSTITUIDO">Sustituido</option>
          </Select>
        </Field>

        <Field label="Correcciones" htmlFor="rp-corrections">
          <Select id="rp-corrections" value={corrections} onChange={(event) => setCorrections(event.target.value)}>
            <option value="">Todos</option>
            <option value="true">Solo con correcciones</option>
            <option value="false">Sin correcciones</option>
          </Select>
        </Field>

        <Field label="Incidencia" htmlFor="rp-incidents">
          <Select id="rp-incidents" value={incidents} onChange={(event) => setIncidents(event.target.value)}>
            <option value="">Todas</option>
            <option value="true">Solo con incidencia</option>
            <option value="false">Sin incidencia</option>
          </Select>
        </Field>

        <Field label="Emitido desde" htmlFor="rp-from">
          <Input id="rp-from" type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} />
        </Field>

        <Field label="Emitido hasta" htmlFor="rp-to">
          <Input id="rp-to" type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} />
        </Field>

        <div className="flex items-end">
          <Button
            onClick={() => {
              setDateFrom("");
              setDateTo("");
              setStatus("");
              setCorrections("");
              setIncidents("");
              setTenantId("");
              setAuthorUserId("");
            }}
          >
            Limpiar filtros
          </Button>
        </div>
      </div>

      {report.isError ? (
        <ErrorNotice error={report.error} onRetry={() => void report.refetch()} />
      ) : (
        <>
          <div className="mb-4 grid gap-4 sm:grid-cols-3">
            <StatCard
              accent="brand"
              icon={<IconDocument />}
              value={report.data?.total ?? 0}
              label="Documentos"
            />
            <StatCard
              accent="violet"
              icon={<IconChecklist />}
              value={report.data?.summary.withCorrections ?? 0}
              label="Con correcciones"
              hint="Automático: más de una versión o sustitución"
            />
            <StatCard
              accent="amber"
              icon={<IconWarning />}
              value={report.data?.summary.withIncidents ?? 0}
              label="Con incidencia"
              hint="Manual: lo marca una persona con sus notas"
            />
          </div>

          {report.data && Object.keys(report.data.summary.byStatus).length > 0 ? (
            <div className="mb-4">
              <Notice tone="neutral">
                Por estado:{" "}
                {Object.entries(report.data.summary.byStatus)
                  .map(([key, value]) => `${decaStatusLabel(key)}: ${value}`)
                  .join(" · ")}
              </Notice>
            </div>
          ) : null}

          {exportReport.isError ? (
            <div className="mb-4">
              <ErrorNotice error={exportReport.error} />
            </div>
          ) : null}

          <DataTable
            columns={columns}
            data={report.data?.rows ?? []}
            isLoading={report.isLoading}
            emptyMessage="No hay documentos que cumplan estos filtros."
          />
        </>
      )}
    </>
  );
}
